// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Sun's real distance and size, proportionally (owner, 2026-10-23/24),
 * shared by the headset's Earth (photorealEarth.ts) and the browser globe
 * (earthTileLayer.ts). One astronomical unit is 149,597,870.7 km and the
 * Earth's radius 6,371 km, so the Sun stands 23,481 Earth radii away and,
 * with a radius of 695,700 km, is 109 Earths across: from the Earth it
 * covers 0.533°, and from anywhere near it (the viewer's own movement
 * shifts its direction by under 0.01°) it covers the same.
 */
export const SUN_DISTANCE_KM = 149_597_870.7
export const SUN_RADIUS_KM = 695_700
export const EARTH_RADIUS_KM = 6371
/** The Sun's distance in Earth radii. */
export const SUN_DISTANCE_RADII = SUN_DISTANCE_KM / EARTH_RADIUS_KM
/** The Sun's radius in Earth radii: 109.2, so it is 109 Earths across. */
export const SUN_RADIUS_RADII = SUN_RADIUS_KM / EARTH_RADIUS_KM
/** The Sun's apparent diameter from the Earth, radians (0.533°). */
export const SUN_ANGULAR_DIAMETER = 2 * Math.asin(SUN_RADIUS_KM / SUN_DISTANCE_KM)

/** The Sun's true distance from a globe of this radius and scale, in scene units. */
export function sunTrueDistance(radius: number, scale = 1): number {
  return radius * scale * SUN_DISTANCE_RADII
}

/** The Sun's true diameter for a globe of this radius and scale: 109 Earths across. */
export function sunTrueDiameter(radius: number, scale = 1): number {
  return 2 * SUN_RADIUS_RADII * radius * scale
}

/**
 * Where the Sun falls on a MapLibre globe's screen (planet radius 1): its
 * place in clip space and its radius in NDC-y units, from the main matrix
 * (projection × view, column-major), the Sun's unit direction from the
 * Earth's centre and the canvas aspect. The Sun is placed at its true
 * distance, 23,481 radii out, so it has no parallax worth drawing and its
 * size is the 0.533° it covers in the view, whatever the zoom. Null when it
 * is behind the camera. `minRadiusNdc` keeps it a few pixels wide in a wide view.
 */
export function sunOnScreen(
  m: ArrayLike<number>,
  dir: readonly [number, number, number],
  aspect: number,
  minRadiusNdc = 0,
): { clipX: number; clipY: number; clipW: number; radiusNdc: number } | null {
  const D = SUN_DISTANCE_RADII
  const clip = (x: number, y: number, z: number) => ({
    x: m[0] * x + m[4] * y + m[8] * z + m[12],
    y: m[1] * x + m[5] * y + m[9] * z + m[13],
    w: m[3] * x + m[7] * y + m[11] * z + m[15],
  })
  const c = clip(dir[0] * D, dir[1] * D, dir[2] * D)
  if (!(c.w > 0)) return null
  // Two directions across the Sun's disc, at its true radius: the screen
  // distance they move is the disc's radius, whichever way the view looks.
  const helper: [number, number, number] = Math.abs(dir[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0]
  const u: [number, number, number] = [
    dir[1] * helper[2] - dir[2] * helper[1],
    dir[2] * helper[0] - dir[0] * helper[2],
    dir[0] * helper[1] - dir[1] * helper[0],
  ]
  const un = Math.hypot(u[0], u[1], u[2])
  u[0] /= un
  u[1] /= un
  u[2] /= un
  const v: [number, number, number] = [
    dir[1] * u[2] - dir[2] * u[1],
    dir[2] * u[0] - dir[0] * u[2],
    dir[0] * u[1] - dir[1] * u[0],
  ]
  let radius = 0
  for (const a of [u, v]) {
    const e = clip(dir[0] * D + a[0] * SUN_RADIUS_RADII, dir[1] * D + a[1] * SUN_RADIUS_RADII, dir[2] * D + a[2] * SUN_RADIUS_RADII)
    if (!(e.w > 0)) return null
    const dx = (e.x / e.w - c.x / c.w) * aspect
    const dy = e.y / e.w - c.y / c.w
    radius += Math.hypot(dx, dy) / 2
  }
  return { clipX: c.x, clipY: c.y, clipW: c.w, radiusNdc: Math.max(radius, minRadiusNdc) }
}
